import { useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'

import { t } from 'i18next'

import { AlertWithTitle } from '~/components/AlertWithTitle'
import HeaderPages from '~/components/HeaderPages'
import PageTitle from '~/components/PageTitle'
import Panel from '~/components/Panel'
import Status from '~/components/Status'
import { formatDateToString } from '~/helpers'
import AdsTable from '~/modules/retailMedia/components/_tables/AdsTable'
import ProductsTable from '~/modules/retailMedia/components/_tables/ProductsTable'
import AudiencePanel from '~/modules/retailMedia/components/AudiencePanel'
import { AudienceSegmentation } from '~/modules/retailMedia/components/AudienceSegmentation'
import Budget from '~/modules/retailMedia/components/Budget'
import CampaignLogs from '~/modules/retailMedia/components/CampaignLogs'
import { InfoView } from '~/modules/retailMedia/components/InfoView'
import { OffsiteReviewActions } from '~/modules/retailMedia/components/OffsiteReviewActions'
import StatusHistory from '~/modules/retailMedia/components/StatusHistory'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'
import { CampaignReadOnlyProvider } from '~/modules/retailMedia/hooks/useCampaignReadOnly'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { useAppSelector } from '~/store/hooks'

const QUEUE_PATH = '/retail-media/campaigns/offsite-review'
const TITLE_PREFIX = 'Retail media - '

/**
 * Revisão de campanha offsite pelo anunciante.
 *
 * Superfície própria, e não um modo da view de campanha: aquela tela é operação
 * e métricas, e o `Banner.tsx` esconde justamente o que interessa aqui quando o
 * status é `pending_review`.
 *
 * Toda a página é leitura, e isso é imposto pelo `CampaignReadOnlyProvider`, não
 * confiado aos painéis: `canEditCampaign` é verdadeiro justamente em
 * `pending_review` para o anunciante, então sem o provider quem vem aprovar
 * ganharia botão de editar o briefing que está aprovando.
 *
 * A única parte que conhece estado, permissão e escrita é o
 * `OffsiteReviewActions`; decidir leva de volta para a fila.
 *
 * Sem gate de `allow_offsite_briefing_campaign`: a rota é `onlyAdvertiser` e o
 * endpoint de detalhe escopa pelo ator do token, então o anunciante só carrega
 * campanha própria. Quem gateia a entrada é a aba da fila.
 */
const OffsiteAdvertiserReviewView = () => {
  const { id } = useParams()

  const [searchParams] = useSearchParams()

  const dispatch = useDispatch()

  const {
    campaign: {
      item: { data, loading, error }
    }
  } = useAppSelector(state => state.retailMedia)

  // A store persiste a última campanha aberta; sem isso a tela pinta o briefing
  // de outra campanha por um frame ao trocar de item na fila.
  const isValidContent = useMemo(() => id === data?.id, [data?.id, id])

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  /**
   * Sem datepicker nesta tela: as datas delimitam só as CTEs de métrica e caem
   * para *hoje* quando omitidas — nenhuma campanha é excluída por elas. Vêm da
   * store apenas para a requisição ficar igual à da view de campanha.
   */
  const queryParams = useMemo(
    () => ({
      campaign_id: id,
      account_info: true,
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD')
    }),
    [startDate, endDate, id]
  )

  useLayoutEffect(() => {
    dispatch(getCampaign(queryParams))
  }, [dispatch, queryParams])

  const title = t('rm:offsiteAdvertiserReview.detail.pageTitle')

  /**
   * Mesmo comportamento da view de campanha: enquanto a store não tiver a
   * campanha da URL, o título diz que está carregando. É o único sinal de
   * estado que aquela tela dá, e divergir aqui só criaria uma segunda convenção.
   *
   * Não passamos `expectedCampaignId` no `getCampaign`: aquela guarda compara o
   * id que **já está** na store, e o caso `pending` a zera, então numa busca de
   * primeiro plano ela descartaria toda resposta. Ela existe para revalidação em
   * segundo plano — ver `store/campaign/reducerCases/get.spec.ts`.
   */
  const pageTitle = useMemo(() => {
    if (error) return `${TITLE_PREFIX}${title}`

    if (!isValidContent) return `${TITLE_PREFIX}${t('rm:Loading')}`

    return `${TITLE_PREFIX}${data?.title || title}`
  }, [error, isValidContent, data?.title, title])

  /**
   * A home (AA-205) lista sem `rmid`. Voltar não depende desse parâmetro na
   * URL da fila: se ele existir, é o escopo em que o revisor estava e já vale
   * no primeiro render (também no erro do GET). Sem ele, o publisher da própria
   * campanha recoloca o "voltar" na fila daquele varejista. Sem os dois, o
   * caminho é a home.
   *
   * Em white label o gate resolve pelo token e o parâmetro a mais é inofensivo.
   */
  const queuePath = useMemo(() => {
    const publisherId =
      searchParams.get('rmid') || (isValidContent ? data?.publisher?.id : null)

    return publisherId ? `${QUEUE_PATH}?rmid=${publisherId}` : QUEUE_PATH
  }, [searchParams, isValidContent, data?.publisher?.id])

  /**
   * A rota é endereçável e o endpoint de detalhe é genérico: nada impede trocar
   * o id na URL pelo de uma campanha onsite. A fila escopa por `type=off_site`,
   * e esta tela precisa do mesmo escopo — a superfície de decisão que ela
   * oferece não existe no fluxo onsite, onde quem revisa é o publisher.
   */
  const isWrongCampaignType = isValidContent && data?.type !== 'off_site'

  const statusData = useMemo(
    () => campaignStatusBadge(data?.status),
    [data?.status]
  )

  if (error || isWrongCampaignType) {
    return (
      <>
        <PageTitle title={pageTitle} />

        <HeaderPages
          title={title}
          backTo={queuePath}
          labelBreadcrumb={t('rm:offsiteAdvertiserReview.detail.back')}
        />

        <AlertWithTitle
          variant="critical"
          title={t(
            isWrongCampaignType
              ? 'rm:offsiteAdvertiserReview.detail.notOffsite'
              : 'rm:offsiteAdvertiserReview.detail.error'
          )}
        >
          {t(
            isWrongCampaignType
              ? 'rm:offsiteAdvertiserReview.detail.notOffsiteSubtitle'
              : 'rm:offsiteAdvertiserReview.detail.errorSubtitle'
          )}
        </AlertWithTitle>
      </>
    )
  }

  return (
    <>
      <PageTitle title={pageTitle} />

      {/* A badge vai no slot ao lado do título, e não em `subtitle`, que é a
          linha de baixo: no desenho o status é leitura da mesma linha do nome da
          campanha. `sideLeftComponent` é o bloco que o header já põe à direita do
          título — o nome diz "left" porque nomeia a posição no código, não na
          tela. No mobile ele empilha, como o resto do cabeçalho. */}
      <HeaderPages
        title={isValidContent ? data?.title : title}
        sideLeftComponent={
          isValidContent ? <Status status={statusData} /> : null
        }
        backTo={queuePath}
        labelBreadcrumb={t('rm:offsiteAdvertiserReview.detail.back')}
      />

      {isValidContent && (
        <CampaignReadOnlyProvider>
          <OffsiteReviewActions
            campaignId={data.id}
            status={data.status}
            message={data.status_history?.[0]?.note}
            canApprove={data.raw?.can_approve}
            queuePath={queuePath}
          />

          <InfoView data={data} />

          {/* Segmentação vem do briefing e existe mesmo sem audiência
                  importada; o painel de audiência só aparece quando houve
                  importação. São coisas diferentes. */}
          <AudienceSegmentation
            targeting={data.raw?.settings?.audience_targeting}
            channel={data.raw?.settings?.channel}
            hasImportedAudience={!!data.audienceId}
          />

          {!!data.audienceId && <AudiencePanel audienceId={data.audienceId} />}

          {/* Sem régua entre os painéis: no desenho o que separa os cards é o
              espaço de um `--sl-space-4`, que a moldura já reserva. */}
          <AdsTable campaignAdType={data.adType} campaignStatus={data.status} />

          <Panel
            template="shoreline"
            title={t('rm:relatedProducts')}
            isCollapsible
            noPaddingBody
          >
            <ProductsTable
              campaignId={data.id}
              publisherId={data.publisher.id}
            />
          </Panel>

          <Budget data={data} loading={loading} />

          {data.status !== 'pending_review' && (
            <StatusHistory data={data.status_history} />
          )}

          <CampaignLogs campaignId={data.id} requestOnLoad />
        </CampaignReadOnlyProvider>
      )}
    </>
  )
}

export default OffsiteAdvertiserReviewView
